// pages/RealtorUpsertAgentProduct.tsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { Media } from "types";
import AgentProductForm, {
  AgentProductFormValues,
  SelectOption,
} from "components/AgentProductForm";
import {
  nameValidation,
  quantityValidation,
} from "validationSchemas/common";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles, } from "components/Imageshelper";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import { getAuthCookie } from "../../utils/authCookies";

type Brand = { id: string; name: string };
type Category = { id: string; name: string };

// Select2 types for agents
type AgentOption = { 
  value: string; 
  label: string; 
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

// Select2 API configuration
const PAGE_SIZE = 10;

type ProductAPI = {
  product: {
    name: string;
    sku: string;
    description: string;
    brand: string;
    category: string;
    item_condition: string;
    package_dimensions?: { length: number; height: number; width: number; weight: number };
    variants: Array<{
      images?: { alt_text: string; url: string }[];
      inventory: Array<{
        warehouse_id?: string;
        bin_id?: string;
        on_hand?: number;
        available: number;
        reserved?: number;
      }>;
    }>;
    threshold_qty: number;
    images?: { alt_text: string; url: string }[];
    status: string;
  };
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const CONDITION_OPTIONS: SelectOption[] = [
  { value: "new", label: "New" },
  { value: "used_like_new", label: "Used - Like New" },
  { value: "used_good", label: "Used - Good" },
  { value: "used_fair", label: "Used - Fair" },
];

export default function RealtorAddEditAgentProduct() {
  const { productId, agentId: agentIdFromRoute } = useParams<{ productId?: string; agentId?: string }>();
  const isEdit = Boolean(productId);
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";

  // ref data
  const [agentProduct, setAgentProduct] = useState<ProductAPI | null>(null);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  
  // State for parent brokerage firm (for teammember login)
  const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
  const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);
  
  // State to track selected agent ID from UI (for add mode)
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");

  // ✅ Compute the agent ID to use for brand-category selector
  // For agent products: use selected agent ID (add mode) or agent_id from product (edit mode)
  // Don't use userId - only use the selected agent ID
  const agentIdForBrandCategory = useMemo(() => {
    // In edit mode, use agent_id from product data if available, otherwise use route agent ID
    if (isEdit) {
      const productAgentId = (agentProduct?.product as any)?.agent_id || agentIdFromRoute || "";
      return productAgentId || agentIdFromRoute || "";
    }
    // In add mode, use selected agent ID from UI, or agentIdFromRoute if available
    return selectedAgentId || agentIdFromRoute || "";
  }, [isEdit, agentProduct, agentIdFromRoute, selectedAgentId]);

  // ✅ Use the brand-category selector hook
  const {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions: brandOptionsFromHook,
    filteredCategories: categoryOptionsFromHook,
    loadingBrands,
    loadingCategories,
  } = useBrandCategorySelector(agentIdForBrandCategory, token || "");

  // Fetch parent hierarchy for teammember login
  const fetchParentHierarchy = async () => {
    if (!isTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "BROKERAGE_FIRM"
        const brokerageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "BROKERAGE_FIRM"
        );
        
        if (brokerageFirm) {
          setParentBrokerageId(brokerageFirm.id || brokerageFirm._id);
          setParentBrokerageName(brokerageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };

  // Fetch parent hierarchy for teammember login
  useEffect(() => {
    if (isTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isTeammember, token]);

  // ===== Step 1: Normalize ID once when building Agent options =====
  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  const mapAgentToOption = (row: any) => ({
    value: normalizeId(row), // ALWAYS store the id in value
    label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
    firstName: row?.firstName || "",
    lastName: row?.lastName || "",
    email: row?.email || "",
    phone: row?.phone1 || "",
  });

  // ===== Agent Loader (Select2 style) =====
  const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue: string, _loadedOptions: readonly AgentOption[], additional: { page: number } | undefined) => {
      const page = additional?.page ?? 1;
      // console.log("AgentLoader firing with:", { page, inputValue });

      try {
        // Build URL with pagination and search
        const params = new URLSearchParams({
          licenseType: "BROKERAGE_AGENT",
          brokerageFirmID: (isTeammember && parentBrokerageId ? parentBrokerageId : userId),
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        const search = (inputValue || "").trim();
        if (search) params.set("search", search);

        const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const responseData = await response.json();
        // console.log('Agent API response:', { page, search, responseData });

        // Handle different response formats
        const results = Array.isArray(responseData) ? responseData : responseData.results || [];
        const hasMore = Array.isArray(responseData)
          ? results.length === PAGE_SIZE
          : responseData.pagination?.more || false;

        // Map to react-select options using normalized ID
        const options: AgentOption[] = results.map(mapAgentToOption);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        console.error("Error loading agent options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, userId, isTeammember, parentBrokerageId]
  );

  // fetch existing (edit)
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/agent/${agentIdFromRoute}/inventory/${productId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to fetch product");
        const data: ProductAPI = await res.json();
        setAgentProduct(data);
        setUploadedFileMeta(data?.product?.variants?.[0]?.images || []);
      } catch (e: any) {
        showError(e.message || "Failed to fetch product");
      }
    })();
  }, [isEdit, productId, agentIdFromRoute, token]);

  // ✅ Brand options from hook (with product's current brand included if not in list)
  const brandOptions: SelectOption[] = useMemo(() => {
    const brandOptions = brandOptionsFromHook;
    const productBrandId = (agentProduct?.product as any)?.brand_id || agentProduct?.product?.brand || "";
    const productBrandName = agentProduct?.product?.brand || "";

    if (productBrandId && productBrandName) {
      const brandExists = brandOptions.some(opt => opt.value === productBrandId);
      if (!brandExists) {
        return [
          { value: productBrandId, label: productBrandName },
          ...brandOptions
        ];
      }
    }

    return brandOptions;
  }, [brandOptionsFromHook, agentProduct]);

  // ✅ Category options from hook (filtered by selected brand, with product's current category included if not in list)
  const categoryOptions: SelectOption[] = useMemo(() => {
    // ✅ Only return categories if brand is selected
    const categoryOptions = selectedBrand ? categoryOptionsFromHook : [];
    const productCategoryId = (agentProduct?.product as any)?.category_id || agentProduct?.product?.category || "";
    const productCategoryName = agentProduct?.product?.category || "";

    if (productCategoryId && productCategoryName) {
      const categoryExists = categoryOptions.some(opt => opt.value === productCategoryId);
      if (!categoryExists) {
        return [
          { value: productCategoryId, label: productCategoryName },
          ...categoryOptions
        ];
      }
    }

    return categoryOptions;
  }, [categoryOptionsFromHook, selectedBrand, agentProduct]);

  async function uploadProductImagesToCloudDrive(): Promise<string[]> {
    const tok = (token || "").trim() || null;
    // use the files you already collected in previewFiles
    const files = (previewFiles || []).map(p => p.file);
    if (!files.length) return [];

    const ids = await uploadFilesAndGetIds(files, tok);
    setPreviewFiles([]);           // clear after successful upload
    return ids;                    // send back to onSubmit
  }
  // If you have a single product object in edit screen:
  const dataForMap = React.useMemo(() => (agentProduct ? agentProduct : []), [agentProduct]);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);


  // initial values
  const initialValues: AgentProductFormValues = useMemo(
    () => ({
      agentId: agentIdFromRoute || "",
      agentName: "",
      agentFirstName: "",
      agentLastName: "",
      name: agentProduct?.product?.name || "",
      sku: agentProduct?.product?.sku || "",
      qty: agentProduct?.product?.variants?.[0]?.inventory?.[0]?.available ?? "",
      threshold_qty: agentProduct?.product?.threshold_qty ?? "",
      brand: selectedBrand || (agentProduct?.product as any)?.brand_id || agentProduct?.product?.brand || "",
      category: selectedCategories[0] || (agentProduct?.product as any)?.category_id || agentProduct?.product?.category || "",
      item_condition: agentProduct?.product?.item_condition || "",
      length: String(agentProduct?.product?.package_dimensions?.length ?? ""),
      height: String(agentProduct?.product?.package_dimensions?.height ?? ""),
      width: String(agentProduct?.product?.package_dimensions?.width ?? ""),
      weight: String(agentProduct?.product?.package_dimensions?.weight ?? ""),
      description: agentProduct?.product?.description || "",
      statuss: agentProduct?.product?.status || "Active",
      images: null,
    }),
    [agentProduct, agentIdFromRoute, selectedBrand, selectedCategories]
  );

  // formik
  const formik = useFormik<AgentProductFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      name: nameValidation,
      sku: Yup.string().required("SKU is required"),
      qty: quantityValidation,
      // threshold_qty: Yup.number().typeError("Enter a number").required("Required"),
      // brand: Yup.string().required("Brand is required"),
      // category: Yup.string().required("Category is required"),
      // item_condition: Yup.string().required("Condition is required"),
    }),
    onSubmit: async (values) => {
      try {
        // Extract brand details from hook options
        const selectedBrandId = String(values.brand || "");
        const selectedBrandOption = brandOptions.find(
          (b) => b.value === selectedBrandId
        );
        const selectedBrandName = selectedBrandOption?.label || "";

        // Extract category details from hook options
        const selectedCategoryId = String(values.category || "");
        const selectedCategoryOption = categoryOptions.find(
          (c) => c.value === selectedCategoryId
        );
        const selectedCategoryName = selectedCategoryOption?.label || "";

        const newImageIds = await uploadProductImagesToCloudDrive();

        // 2) Convert *existing* (kept) images from state as-is
        const kept: Media[] = (uploadedFileMeta ?? []).map((m: any) => ({
          url: m?.url,
          alt_text: m?.alt_text || "",
          default: !!m?.default,
        }));

        // 3) Convert *new* uploads into Media[]
        const newMedia: Media[] = newImageIds.map((id, idx) => ({
          url: id,
          alt_text: (previewFiles?.[idx]?.file?.name as string) || "",
          default: false,
        }));

        // 4) Merge and ensure exactly one default (if none, make the first one default)
        let images: Media[] = [...kept, ...newMedia];
        if (!images.some((m) => m.default) && images.length > 0) {
          images = images?.map((m, i) => ({ ...m, default: i === 0 }));
        }

        try {
          if (pendingDeleteIds.length > 0) {
            await hardDeletePresignedFiles(pendingDeleteIds, token || null);
            setPendingDeleteIds([]); // clear
          }
        } catch (delErr: any) {
          console.error("Image hard delete (batch) failed:", delErr);
          // Don't block success — just inform
          showError(delErr?.message || "Some removed images could not be deleted.");
        }


        // ADD
        if (!isEdit) {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/agent/${selectedAgentId}/inventory`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                product: {
                  name: values.name,
                  sku: values.sku,
                  description: values.description,
                  brand: selectedBrandName,
                  brand_id: selectedBrandId,
                  category: selectedCategoryName,
                  category_id: selectedCategoryId || null,
                  item_condition: values.item_condition,
                  purchase_price: 1,
                  retail_price: 1,
                  package_dimensions: {
                    length: Number(values.length),
                    height: Number(values.height),
                    width: Number(values.width),
                    weight: Number(values.weight),
                  },
                  variants: [
                    {
                      variant_name: values.name,
                      sku: values.sku,
                      description: values.description,
                      images: images,
                      package_dimensions: {
                        length: Number(values.length),
                        height: Number(values.height),
                        width: Number(values.width),
                        weight: Number(values.weight),
                      },
                      threshold_qty: Number(values.threshold_qty),
                      inventory: [
                        {
                          on_hand: Number(values.qty),
                          available: Number(values.qty),
                          reserved: 0,
                        },
                      ],
                      barcode: "",
                      is_rotational: false,
                    },
                  ],
                  threshold_qty: Number(values.threshold_qty),
                  images: images || [],
                  status: values.statuss,
                  is_rotational: false,
                  linked_mode: "none",
                  locked_status: "unlocked",
                  linked_installer: null,
                },
              }),
            }
          );
          if (!response.ok) throw new Error("Failed to save data");
          await response.json();
          showSuccess("Product added successfully!");
          navigate(`${baseRoute}/agentInventory`);
          return;
        }

        // EDIT
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/agent/${agentIdFromRoute}/inventory/${productId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              product: {
                name: values.name,
                sku: values.sku,
                description: values.description,
                brand: selectedBrandName,
                brand_id: selectedBrandId,
                category: selectedCategoryName,
                category_id: selectedCategoryId || null,
                item_condition: values.item_condition,
                purchase_price: 1,
                retail_price: 1,
                package_dimensions: {
                  length: Number(values.length),
                  height: Number(values.height),
                  width: Number(values.width),
                  weight: Number(values.weight),
                },
                variants: [
                  {
                    variant_name: values.name,
                    sku: values.sku,
                    description: values.description,
                    images: images,
                    package_dimensions: {
                      length: Number(values.length),
                      height: Number(values.height),
                      width: Number(values.width),
                      weight: Number(values.weight),
                    },
                    threshold_qty: Number(values.threshold_qty),
                    inventory:
                      agentProduct?.product?.variants?.[0]?.inventory?.map((inv) => {
                        const { on_hand, ...keep } = inv; // keep structure but override available
                        return { ...keep, available: Number(values.qty) };
                      }) || [],
                    is_rotational: false,
                  },
                ],
                threshold_qty: Number(values.threshold_qty),
                images: images,
                status: values.statuss,
                is_rotational: false,
                linked_mode: "none",
                locked_status: "unlocked",
                linked_installer: null,
                installer_notes: "",
              },
            }),
          }
        );
        if (!response.ok) throw new Error("Failed to save data");
        await response.json();
        showSuccess("Product updated successfully!");
        navigate(-1);
      } catch (err: any) {
        showError(err.message || "Failed to save Product.");
      }
    },
  });

  // ✅ Track selected agent ID from form (for add mode) - update when user selects agent
  useEffect(() => {
    // In add mode, update selectedAgentId when user selects an agent in the form
    if (!isEdit && formik.values.agentId && formik.values.agentId !== selectedAgentId) {
      setSelectedAgentId(formik.values.agentId);
    }
  }, [formik.values.agentId, isEdit, selectedAgentId]);

  const handleAddFiles = (files: File[]) => {
    if (!files?.length) return;
    const fresh = files?.map(file => ({ file, url: URL.createObjectURL(file) }));
    setPreviewFiles(prev => [...prev, ...fresh]);
  };

  // Remove a preview by its index (revoke URL + pop from state)
  const handleRemovePreview = (index: number) => {
    setPreviewFiles(prev => {
      const next = [...prev];
      const it = next[index];
      if (it?.url) URL.revokeObjectURL(it.url);
      next.splice(index, 1);
      return next;
    });
  };

  // Remove an already uploaded image **by index** (queue id for hard delete and update UI)
  const handleRemoveUploaded = (index: number) => {
    const id = String(uploadedFileMeta?.[index]?.url || "");
    if (!id) return;
    // queue for delete on save
    setPendingDeleteIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    // update UI now
    setUploadedFileMeta(prev => prev?.filter((_, i) => i !== index));
  };

  // ✅ Refs to prevent circular updates and track previous values
  const isSyncingFromFormik = useRef(false);
  const isSyncingFromHook = useRef(false);
  const prevProductBrandId = useRef<string>("");
  const prevProductCategoryId = useRef<string>("");
  const prevFormikBrand = useRef<string>("");
  const prevFormikCategory = useRef<string>("");
  const prevSelectedBrand = useRef<string>("");
  const prevSelectedCategory = useRef<string>("");

  // ✅ Sync product's brand/category to hook when product loads (Edit mode) - Only once
  useEffect(() => {
    const productBrandId = (agentProduct?.product as any)?.brand_id || agentProduct?.product?.brand || "";
    if (productBrandId && productBrandId !== prevProductBrandId.current && productBrandId !== selectedBrand) {
      prevProductBrandId.current = productBrandId;
      // console.log("🔄 [Product→Hook] Setting brand from product:", productBrandId);
      setSelectedBrand(productBrandId);
    }
  }, [agentProduct, selectedBrand]);

  useEffect(() => {
    const productCategoryId = (agentProduct?.product as any)?.category_id || agentProduct?.product?.category || "";
    if (productCategoryId && productCategoryId !== prevProductCategoryId.current) {
      const currentFirst = selectedCategories[0] || "";
      if (productCategoryId !== currentFirst) {
        prevProductCategoryId.current = productCategoryId;
        // console.log("🔄 [Product→Hook] Setting category from product:", productCategoryId);
        setSelectedCategories([productCategoryId]);
      }
    }
  }, [agentProduct, selectedCategories]);

  // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const brandValue = formik.values.brand || "";
    if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
      prevFormikBrand.current = brandValue;
      // console.log("🔄 [Formik→Hook] Brand changed:", brandValue);
      isSyncingFromFormik.current = true;
      setSelectedBrand(brandValue);
      setSelectedCategories([]); // Clear categories when brand changes
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.brand, selectedBrand]);

  // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const categoryValue = formik.values.category || "";
    const currentFirst = selectedCategories[0] || "";
    if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
      prevFormikCategory.current = categoryValue;
      // console.log("🔄 [Formik→Hook] Category changed:", categoryValue);
      isSyncingFromFormik.current = true;
      setSelectedCategories([categoryValue]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.category, selectedCategories]);

  // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
      prevSelectedBrand.current = selectedBrand;
      // console.log("🔄 [Hook→Formik] Brand changed:", selectedBrand);
      isSyncingFromHook.current = true;
      formik.setFieldValue("brand", selectedBrand, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    }
  }, [selectedBrand, formik.values.brand]);

  // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    const firstCategory = selectedCategories[0] || "";
    if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
      prevSelectedCategory.current = firstCategory;
      // console.log("🔄 [Hook→Formik] Category changed:", firstCategory);
      isSyncingFromHook.current = true;
      formik.setFieldValue("category", firstCategory, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    } else if (!firstCategory && formik.values.category) {
      // Clear category if hook has no selection
      if (prevSelectedCategory.current !== "") {
        prevSelectedCategory.current = "";
        // console.log("🔄 [Hook→Formik] Clearing category");
        isSyncingFromHook.current = true;
        formik.setFieldValue("category", "", false);
        setTimeout(() => { isSyncingFromHook.current = false; }, 0);
      }
    }
  }, [selectedCategories, formik.values.category]);

  // Create empty agentOptions for fallback - now using AsyncPaginate
  const agentOptions: SelectOption[] = [];

  return (
    <AgentProductForm
      isEdit={isEdit}
      formik={formik as any}
      agentOptions={agentOptions}
      loadAgentOptions={loadAgentOptions}
      brandOptions={brandOptions}
      categoryOptions={categoryOptions}
      conditionOptions={CONDITION_OPTIONS}
      statusOptions={STATUS_OPTIONS}
      previewFiles={previewFiles}
      uploadedFiles={uploadedFileMeta}
      onAddFiles={handleAddFiles}
      onRemovePreview={handleRemovePreview}
      onRemoveUploaded={handleRemoveUploaded}
      title={isEdit ? "Edit Product" : "Add Product"}
      onBack={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      submitting={formik.isSubmitting}
      baseUrl={process.env.REACT_APP_BASE_URL}
      resolveSrc={(url) =>
        getImageSrc(url, imageUrlMap) ||
        (String(url).startsWith("http") ? String(url)
          : `${process.env.REACT_APP_BASE_URL}/${String(url || "").replace(
            /^https?:\/\/[^/]+\/(uploads\/.*)/, '$1')}`)
      }
      isTeammember={isTeammember}
      parentBrokerageId={parentBrokerageId}
    />
  );
}
